import { SvcAddProductToOrder } from "@springtree/eva-services-core";
import { HTTPError } from "ky";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { SubmitHandler } from "react-hook-form";

import IconChevronLeft from "@/icons/icon-chevron-left";
import Button from "~/components/common/button";
import Grid from "~/components/common/grid";
import Icon from "~/components/common/icon";
import LinkWrapper from "~/components/common/link-wrapper";
import SkeletonProductTile from "~/components/common/skeletons/skeleton-product-tile";
import { useWishlist } from "~/contexts/wishlist";
import { useEvaCartAdd } from "~/hooks/use-eva-cart";
import { useGlobalNotification } from "~/hooks/use-global-notification";
import Logger from "~/services/logger/logger";
import { wishlistNameFields } from "~/shared/wishlist/wishlist.model";
import appRoutes from "~/utils/app-routes";
import productUtils from "~/utils/product-utils";

import DeleteWishlistDialog from "./delete-wishlist-dialog";
import EditWishlistDialog from "./edit-wishlist-dialog";
import styles from "./wishlist-detail.module.scss";

const ProductTile = dynamic(() => import("~/components/products/product-tile"), {
  loading: () => <SkeletonProductTile />,
});

type Props = {
  wishlistId: string;
};

export default function WishlistDetail(props: Props) {
  const t = useTranslations();
  const router = useRouter();

  const [isEditWishlistOpen, setIsEditWishlistOpen] = useState(false);
  const [isDeleteWishlistOpen, setIsDeleteWishlistOpen] = useState(false);
  const [isAddAllToBagClicked, setIsAddAllToBagClicked] = useState(false);

  const { newInfoGlobalNotification, closeGlobalNotification, newErrorGlobalNotification } = useGlobalNotification();

  const { list, removeProdFromWishlist, refetch, updateWishlist, deleteWishlist } = useWishlist();

  const shoppingCartAdd = useEvaCartAdd(SvcAddProductToOrder);

  const [wishListUpdateError, setWishListUpdateError] = useState<any>();
  const [wishListDeleteError, setWishListDeleteError] = useState<any>();

  const currentList = list?.find((el) => el.ID == props.wishlistId);
  const currentListWithValidPrice = useMemo(
    () => currentList?.Products?.filter((product) => productUtils.hasValidDisplayPrice(product)),
    [currentList]
  );

  const updateWishlistHandler: SubmitHandler<wishlistNameFields> = async (submitData: wishlistNameFields) => {
    setWishListUpdateError(undefined);

    if (currentList) {
      await updateWishlist({
        wishlistName: submitData.wishlistName,
        wishlistId: `${props.wishlistId}`,
        onSuccess: async () => {
          setWishListUpdateError(undefined);
          setIsEditWishlistOpen(false);
          refetch();
        },
        onError: async (error) => {
          if (error?.name === "HTTPError") {
            const jsonError = await (error as HTTPError).response.json();
            setWishListUpdateError(jsonError);
          }
        },
      });
    }
  };

  const deleteWishlistHandler = async (confirm?: boolean) => {
    if (confirm && currentList) {
      await deleteWishlist({
        wishlistId: `${currentList.ID}`,
        onSuccess: async () => {
          setWishListDeleteError(undefined);
          setIsDeleteWishlistOpen(false);
          router.push(appRoutes.MYACCOUNT_WISHLIST);
          refetch();
        },
        onError: async (error) => {
          if (error?.name === "HTTPError") {
            const jsonError = await (error as HTTPError).response.json();
            setWishListDeleteError(jsonError);
          }
        },
      });
    } else {
      setIsDeleteWishlistOpen(false);
    }
  };

  const moveAllToBag = () => {
    setIsAddAllToBagClicked(true);
    let list = currentList
      ? currentList.Products?.filter((p) => p !== null && p.has_stock && productUtils.hasValidDisplayPrice(p)).map(
          (p) => p.product_id
        )
      : null;

    // if the length of the list of filtered products is different than the list of unfiltered products,
    // it means that some products will not be added to the cart, so show a notification error
    if (currentList?.Products && currentList.Products.length > 0 && list?.length !== currentList?.Products?.length) {
      newInfoGlobalNotification({
        slug: "someProductsNotAddedToBag",
        closeOnClickCta: true,
        ctaCaption: "generic.ok",
      });
    } else {
      closeGlobalNotification();
    }

    if (list && list.length > 0) {
      list.map((prd) => {
        shoppingCartAdd({
          orderType: 0,
          payload: {
            ProductID: prd,
            QuantityOrdered: 1,
          },
        })
          .then(async (data) => {
            if (!!data) {
              await removeProdFromWishlist(prd, currentList!.ID).then((data) => {
                if (!data) throw new Error("removeProdFromWishlist errored");
              });
            } else {
              throw new Error("shoppingCartAdd errored");
            }
          })
          .catch((error) => {
            Logger.instance.error("WishlistDetail > moveAllToBag errored:", error);
            newErrorGlobalNotification({
              slug: "cartGenericError",
              closeOnClickCta: true,
              ctaCaption: "generic.cancel",
            });
          });
      });
    }
  };

  if (!list || !currentList || currentList?.Products?.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className={styles.wishlistDetailsWrapper}>
        <LinkWrapper href={`wishlist`} className={styles.wishlistDetailsTitle}>
          <Icon icon={IconChevronLeft} aria-hidden width={20} height={20} className={styles.orderDetailsTitleIcon} />
        </LinkWrapper>
        <h2>{currentList.Name}</h2>
        <Button lookAsLink onClick={() => setIsEditWishlistOpen(true)}>
          {t("generic.edit")}
        </Button>
        <Button lookAsLink onClick={() => setIsDeleteWishlistOpen(true)}>
          {t("generic.wishlist.delete")}
        </Button>
      </div>

      <div
        className={styles.a11ySection}
        aria-live="polite"
        aria-relevant="removals"
        aria-hidden={isAddAllToBagClicked}
      >
        {currentList.Products?.filter((value) => value != null)?.map((product) => (
          <p key={`${product.product_id}-a11y`}>
            {t.rich("generic.productRemovedWishlist", {
              0: product.display_value,
            })}
          </p>
        ))}
      </div>
      <div className={styles.a11ySection} aria-live="polite" aria-relevant="removals">
        {!isAddAllToBagClicked ? <p> {t("generic.addedAllProducts")} </p> : null}
      </div>

      <Grid className={styles.grid}>
        {currentList.Products?.length == 0 && <p className={styles.noProductsYet}>{t("generic.noProductsYet")}</p>}
        {currentList.Products?.filter((value) => value != null)?.map((product) => (
          <div key={product.product_id} className={styles.tile}>
            <ProductTile product={product} />
          </div>
        ))}
      </Grid>
      {/* <Button
        className={styles.testBtn}
        onClick={() => addProductToWishlist({ ID: data.ID, ProductIDs: [54, 602, 119] }).then(() => refetch())}
        variant="secondary"
      >
        Add products for testing
      </Button>
      <Button
        className={styles.testBtn}
        onClick={() => addProductToWishlist({ ID: data.ID, ProductIDs: [119] }).then(() => refetch())}
        variant="secondary"
      >
        Add 1 product for testing
      </Button> */}
      {typeof currentList?.Products?.length === "number" && currentList?.Products?.length > 0 ? (
        <Button
          disabled={!currentListWithValidPrice?.length}
          aria-disabled={!currentListWithValidPrice?.length}
          className={styles.testBtn}
          onClick={() => moveAllToBag()}
          variant="secondary"
        >
          {t("generic.addAllToBag")}
        </Button>
      ) : null}
      <EditWishlistDialog
        isOpen={isEditWishlistOpen}
        onClose={() => setIsEditWishlistOpen(false)}
        defaultName={currentList.Name || ""}
        submit={updateWishlistHandler}
        error={wishListUpdateError}
        cleanError={() => setWishListUpdateError(undefined)}
      />
      <DeleteWishlistDialog
        isOpen={isDeleteWishlistOpen}
        onClose={() => setIsDeleteWishlistOpen(false)}
        error={wishListDeleteError}
        cleanError={() => setWishListDeleteError(undefined)}
        onSubmit={deleteWishlistHandler}
      />
    </>
  );
}
